import { forwardRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import type { AutocompleteV2Props, AutocompleteOption } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  popper: {
    zIndex: theme.zIndex.modal,
  },

  paper: {
    marginTop: theme.spacing(1),
    boxShadow: '0px 8px 40px rgba(133, 133, 133, 0.2)',
    border: `1px solid ${theme.palette.darkGrey[15]}`,
    borderRadius: theme.shape.borderRadius,
  },

  listbox: {
    maxHeight: '40vh',
    overflow: 'auto',
    padding: '8px 0',
  },

  option: {
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    fontSize: 14,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.palette.darkGrey[5],
    },

    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.darkGrey[5],
    },

    '&[data-focus="true"]': {
      backgroundColor: theme.palette.darkGrey[5],
    },
  },

  noOptions: {
    color: theme.palette.text.secondary,
    padding: '12px 16px',
    fontSize: 14,
  },

  endAdornment: {
    top: 'calc(50% - 12px)',
    right: 14,
  },

  popupIndicator: {
    padding: 4,
    '&[data-popup-open="true"] svg': {
      transform: 'rotate(180deg)',
    },
  },

  clearIndicator: {
    padding: 4,
    marginRight: 4,
  },
}));

/**
 * AutocompleteV2 - Modern autocomplete component based on MUI v5 standards
 *
 * This component follows Material UI v5 best practices and provides a consistent
 * API with the design system. It uses the native MUI Autocomplete component
 * with custom styling and enhanced TypeScript support.
 *
 * @example
 * <AutocompleteV2
 *   label="Select Options"
 *   options={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   getOptionLabel={(option) => option.name}
 *   multiple
 * />
 */
const AutocompleteV2 = forwardRef<HTMLDivElement, AutocompleteV2Props<AutocompleteOption>>(function AutocompleteV2(
  {
    label,
    placeholder,
    helperText,
    errorMsg,
    error = false,
    loading = false,
    required = false,
    hideLabel = false,
    hideLabelHelperText = false,
    'data-testid': dataTestId = 'autocomplete-v2',
    className,
    ...autocompleteProps
  },
  ref,
) {
  const classes = useStyles();

  return (
    <Autocomplete
      className={className}
      classes={{
        popper: classes.popper,
        paper: classes.paper,
        listbox: classes.listbox,
        option: classes.option,
        noOptions: classes.noOptions,
      }}
      loading={loading}
      loadingText="Cargando..."
      noOptionsText="No hay opciones disponibles"
      ref={ref}
      renderInput={(params) => {
        let helperTextToShow;
        if (hideLabelHelperText) {
          helperTextToShow = undefined;
        } else if (error) {
          helperTextToShow = errorMsg;
        } else {
          helperTextToShow = helperText;
        }

        return (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <div className={classes.endAdornment}>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </div>
              ),
            }}
            data-testid={dataTestId}
            error={error}
            fullWidth
            helperText={helperTextToShow}
            label={hideLabel ? undefined : label}
            placeholder={placeholder}
            required={required}
            variant="outlined"
          />
        );
      }}
      {...autocompleteProps}
    />
  );
});

export default AutocompleteV2;
